import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const NAV_LINKS = [
  { label: "Home", href: "#top" },
  { label: "Pricing", href: "#pricing" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const onLanding = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToPricing = (e: React.MouseEvent) => {
    if (!onLanding) return; // let Link/anchor behave normally on other pages
    e.preventDefault();
    const el = document.getElementById("pricing");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="wc-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled || !onLanding
          ? "bg-white/85 backdrop-blur-xl border-b border-gray-100 shadow-sm text-[#30265b]"
          : "bg-transparent text-white"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="wc-navbar-logo"
          aria-label="WinCampaign — home"
          className="flex items-center group"
        >
          <img
            src="/wincampaign-logo.webp"
            alt="WinCampaign"
            className="h-7 sm:h-8 w-auto"
          />
        </Link>

        {onLanding && (
          <nav className="hidden md:flex items-center gap-8 font-nav text-sm font-medium">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => {
                  if (l.href === "#top") {
                    e.preventDefault();
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  } else if (l.href === "#pricing") {
                    scrollToPricing(e);
                  }
                }}
                data-testid={`wc-nav-${l.href.replace("#", "")}`}
                className={`transition-colors ${
                  scrolled
                    ? "text-[#30265b]/80 hover:text-[#673dfc]"
                    : "text-white/80 hover:text-[#d3fc3a]"
                }`}
              >
                {l.label}
              </a>
            ))}
          </nav>
        )}

        <div className="hidden md:flex items-center">
          {onLanding ? (
            <a
              href="#pricing"
              onClick={scrollToPricing}
              data-testid="wc-navbar-cta"
              className="bg-[#d3fc3a] text-[#1a1a24] font-display font-semibold text-sm rounded-full px-5 py-2.5 hover:-translate-y-0.5 hover:shadow-lg transition-transform duration-300"
            >
              Yes, Help Me Build My $10K/Month Business
            </a>
          ) : (
            <Link
              to="/"
              data-testid="wc-navbar-cta"
              className="bg-[#d3fc3a] text-[#1a1a24] font-display font-semibold text-sm rounded-full px-5 py-2.5 hover:-translate-y-0.5 hover:shadow-lg transition-transform duration-300"
            >
              Back to Home
            </Link>
          )}
        </div>

        <button
          aria-label="Toggle menu"
          data-testid="wc-navbar-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className={`md:hidden w-10 h-10 grid place-items-center rounded-full ${
            scrolled || !onLanding ? "text-[#30265b]" : "text-white"
          }`}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-white border-t border-gray-100 px-5 py-4 space-y-3">
          {onLanding &&
            NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => {
                  setOpen(false);
                  if (l.href === "#top") {
                    e.preventDefault();
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  } else if (l.href === "#pricing") {
                    scrollToPricing(e);
                  }
                }}
                data-testid={`wc-nav-mobile-${l.href.replace("#", "")}`}
                className="block font-nav font-medium text-[#30265b]"
              >
                {l.label}
              </a>
            ))}
          {onLanding ? (
            <a
              href="#pricing"
              onClick={(e) => {
                setOpen(false);
                scrollToPricing(e);
              }}
              data-testid="wc-navbar-mobile-cta"
              className="block text-center bg-[#d3fc3a] text-[#1a1a24] font-display font-semibold rounded-full px-5 py-3"
            >
              Yes, Help Me Build My $10K/Month Business
            </a>
          ) : (
            <Link
              to="/"
              onClick={() => setOpen(false)}
              data-testid="wc-navbar-mobile-cta"
              className="block text-center bg-[#d3fc3a] text-[#1a1a24] font-display font-semibold rounded-full px-5 py-3"
            >
              Back to Home
            </Link>
          )}
        </div>
      )}
    </header>
  );
}
